import { convertFromUsd, getDisplayCurrency } from "./displayCurrency.js";

const formatterCache = new Map();

const createFormatter = (currency, options) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      ...options,
    });
  } catch {
    return {
      format: (value) => {
        const amount = Number.isFinite(value)
          ? value.toFixed(options?.maximumFractionDigits ?? 2)
          : String(value);
        return `${currency} ${amount}`;
      },
    };
  }
};

export const getCurrencyFormatter = (currency, options = {}) => {
  const normalizedCurrency = typeof currency === "string" && currency.trim()
    ? currency.trim().toUpperCase()
    : "USD";
  const maxDigits =
    typeof options.maximumFractionDigits === "number"
      ? options.maximumFractionDigits
      : normalizedCurrency === "JPY"
        ? 0
        : 2;
  // useGrouping is part of the key, not just the options. Formatters are
  // shared by currency + digits, so without it the first caller for a given
  // pair would decide grouping for every later one — catalog prices asking for
  // no separators would silently strip them from checkout totals too, or the
  // reverse, depending purely on which rendered first.
  const grouping = options.useGrouping !== false;
  const cacheKey = `${normalizedCurrency}|${maxDigits}|${grouping ? "g" : "n"}`;
  if (!formatterCache.has(cacheKey)) {
    formatterCache.set(
      cacheKey,
      createFormatter(normalizedCurrency, { ...options, maximumFractionDigits: maxDigits }),
    );
  }
  return formatterCache.get(cacheKey);
};

export const formatCurrency = (value, currency, options = {}) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  const formatter = getCurrencyFormatter(currency, options);
  return formatter.format(value);
};

// Catalog/display formatter — drops the trailing .00 on whole-dollar amounts
// (e.g. 25 -> "$25", 25.5 -> "$25.50", 1950 -> "$1,950"). Every currency
// keeps the separator its locale uses. Use everywhere prices are shown
// outside checkout/cart-total/order/billing contexts, where cents matter.
export const formatDisplayPrice = (value, currency, options = {}) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  // Catalog prices follow the header's currency selector. Only USD-denominated
  // values convert: a caller that asks for a specific other currency (e.g. a
  // listing's original JPY price) is showing that currency deliberately, and
  // re-converting it would be wrong.
  let amount = value;
  let code = currency;
  const display = getDisplayCurrency();
  if (display.currency !== "USD" && String(currency).toUpperCase() === "USD") {
    amount = convertFromUsd(value);
    code = display.currency;
  }
  // Zero-decimal currencies (JPY, KRW, VND...) are handled by
  // getCurrencyFormatter, which already drops the fraction digits for them.
  const rounded = Math.round(amount * 100) / 100;
  const isWhole = Number.isInteger(rounded);
  // Every currency keeps the thousands separator its locale uses — a comma,
  // a period or a space, whichever Intl picks — USD included ($1,950). USD
  // used to run its digits together as a house style; it now groups like the
  // rest. A caller can still opt out by passing useGrouping: false, which the
  // spread above carries through.
  return formatCurrency(rounded, code, {
    ...options,
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2,
  });
};
